<script>
  import { onMount, tick, getContext } from "svelte";
  import Header from "./Header.svelte";
  import { albums } from "./albums";

  const { close } = getContext("simple-modal");

  const closeOnSelf = (e) => {
    if (e.target === e.currentTarget) close();
  };

  export let album;
  // Ordered sibling album slugs (e.g. the series being browsed); arrowing
  // past either end of this album continues into the adjacent one.
  export let context = null;

  let albumInfo;
  let images;
  $: albumInfo = albums.find((a) => a.album === album);
  $: images = albumInfo.images || [1, 2, 3, 4];

  let currentImage = 1;

  const selectImage = (image) => {
    currentImage = image;
  };

  // Warm the cache for the adjacent albums so stepping across gallery
  // boundaries doesn't trickle in image by image.
  const preload = (src) => {
    const im = new Image();
    im.src = src;
  };
  const preloadNeighbors = (a) => {
    if (!context || context.length < 2) {
      return;
    }
    const ai = context.indexOf(a);
    for (const d of [1, -1]) {
      const slug = context[(ai + d + context.length) % context.length];
      const imgs = imagesOf(slug);
      for (const n of imgs) {
        preload(`/images/${slug}/${n}_strip.jpeg`);
      }
      // the images a step in that direction would land on
      preload(`/images/${slug}/${d > 0 ? imgs[0] : imgs[imgs.length - 1]}.jpeg`);
    }
  };
  $: preloadNeighbors(album);

  // A swipe to the adjacent image is the likeliest next action; keep both
  // neighbors of the current image warm too.
  const preloadAdjacentImages = (a, current) => {
    const list = imagesOf(a);
    const idx = list.indexOf(current);
    for (const n of [list[idx + 1], list[idx - 1]]) {
      if (n !== undefined) {
        preload(`/images/${a}/${n}.jpeg`);
      }
    }
  };
  $: preloadAdjacentImages(album, currentImage);

  // Keep the active thumb visible when navigation moves it off-screen.
  let thumbEls = {};
  const scrollActiveIntoView = async () => {
    await tick();
    const el = thumbEls[currentImage];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }
  };
  $: currentImage, album, scrollActiveIntoView();

  const imagesOf = (slug) => {
    const info = albums.find((a) => a.album === slug);
    return info.images || [1, 2, 3, 4];
  };

  const step = (direction) => {
    const idx = images.indexOf(currentImage) + direction;
    if (idx >= 0 && idx < images.length) {
      currentImage = images[idx];
      return;
    }
    if (!context || context.length < 2) {
      return;
    }
    const ai = context.indexOf(album);
    const next =
      context[(ai + direction + context.length) % context.length];
    album = next;
    const nextImages = imagesOf(next);
    currentImage = direction > 0 ? nextImages[0] : nextImages[nextImages.length - 1];
  };

  // Horizontal swipes step through images like the arrow keys; a swipe must
  // be decisively horizontal so vertical scrolling is left alone.
  let touchX = null;
  let touchY = null;
  const onTouchStart = (e) => {
    touchX = e.touches[0].clientX;
    touchY = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchX === null) {
      return;
    }
    const dx = e.changedTouches[0].clientX - touchX;
    const dy = e.changedTouches[0].clientY - touchY;
    touchX = null;
    touchY = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      step(dx < 0 ? 1 : -1);
    }
  };

  onMount(() => {
    const listener = (event) => {
      if (event.keyCode === 39) {
        step(1);
      }
      if (event.keyCode === 37) {
        step(-1);
      }
    };

    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  });
</script>

<div class="main">
  <Header>
    <div class="header-info">
      <i>{albumInfo.name}</i>, {albumInfo.year}{#if albumInfo.size}, {albumInfo.size}{/if}
    </div>
  </Header>

  <div
    class="main-image"
    on:click={closeOnSelf}
    on:touchstart={onTouchStart}
    on:touchend={onTouchEnd}
  >
    <img src="/images/{album}/{currentImage}.jpeg" alt={albumInfo.name} />
  </div>

  <div class="thumbs">
    {#each images as image (album + "/" + image)}
      <span
        class="thumb-wrap"
        class:active={image === currentImage}
        bind:this={thumbEls[image]}
      >
        <img
          class="thumb"
          src="/images/{album}/{image}_strip.jpeg"
          alt="{albumInfo.name} view {image}"
          on:click={() => selectImage(image)}
        />
      </span>
    {/each}
  </div>
</div>

<style>
  .main {
    display: flex;
    flex-direction: column;
  }
  .main-image {
    display: flex;
    justify-content: center;
    /* 80px is the height of .thumbs and 55px is the height of .album-info */
    height: calc(var(--app-height) - 80px - 55px);
  }
  .main-image img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  .thumbs {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
    height: 80px;
    max-width: 100vw;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    padding: 0 10px;
    box-sizing: border-box;
  }
  /* Centers the strip when it fits, while keeping the start reachable
     when it overflows (justify-content: center would clip it). */
  .thumb-wrap:first-child {
    margin-left: auto;
  }
  .thumb-wrap:last-child {
    margin-right: auto;
  }
  .thumb-wrap {
    position: relative;
    display: flex;
    flex-shrink: 0;
  }
  /* Four corner brackets marking the selected thumbnail */
  .thumb-wrap.active::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(#333, #333) left top / 1px 10px,
      linear-gradient(#333, #333) left top / 10px 1px,
      linear-gradient(#333, #333) right top / 1px 10px,
      linear-gradient(#333, #333) right top / 10px 1px,
      linear-gradient(#333, #333) left bottom / 1px 10px,
      linear-gradient(#333, #333) left bottom / 10px 1px,
      linear-gradient(#333, #333) right bottom / 1px 10px,
      linear-gradient(#333, #333) right bottom / 10px 1px;
    background-repeat: no-repeat;
  }
  .thumb {
    cursor: pointer;
    max-width: 80px;
    max-height: 80px;
  }
  .header-info {
    font-size: 1rem;
  }
  @media (max-width: 600px) {
    .header-info {
      font-size: 0.8rem;
    }
  }
</style>
